import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Check, Calendar, BookOpen, Users, MessageCircle, Target, GraduationCap, LineChart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import type { AutoplayType } from "embla-carousel-autoplay";
import { HowWeWork } from "@/components/HowWeWork";
import { useNavigation } from "@/hooks/use-navigation";

export const YksKoclugu = () => {
  const navigate = useNavigation();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const textRevealVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: 0.8,
        ease: "easeOut",
      },
    },
  };

  const plugin = Autoplay({ delay: 4000, stopOnInteraction: false }) as AutoplayType;

  const steps = [
    {
      title: "1. Tanışma ve Analiz",
      content: "İlk görüşmemizde mevcut durumunuzu, hedeflerinizi ve çalışma alışkanlıklarınızı detaylıca analiz ederiz.",
      image: "/kocluk-nedir-materials/analiz.png",
      badge: "Ücretsiz İlk Görüşme"
    },
    {
      title: "2. Program Oluşturma",
      content: "Analiz sonuçlarına göre size özel bir çalışma programı ve strateji belirleriz.",
      image: "/kocluk-nedir-materials/ders.png",
      badge: "Kişiye Özel Program"
    },
    {
      title: "3. Düzenli Takip",
      content: "Haftalık görüşmeler ve günlük iletişimle sürecinizi yakından takip eder, gerekli düzenlemeleri yaparız. Detaylı deneme analizleri ile eksiklerinizi saptar ve sorunu ortadan kaldırmaya yönelik programlar düzenleriz.",
      image: "/kocluk-nedir-materials/deneme.png",
      badge: "Haftalık Online Görüşme"
    },
    {
      title: "4. Sürekli Destek ve Günlük Mesaj Takibi",
      content: "Sadece akademik değil, motivasyon ve stres yönetimi konularında da yanınızda oluruz. Günlük mesaj takipleriyle öğrenciyi hep kontrol ederiz. Çalışma sürekliliğini alışkanlık haline getiririz.",
      image: "/kocluk-nedir-materials/destek.png",
      badge: "Günlük Mesaj Takibi"
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-24">
      <div className="w-full px-4">
        {/* Hero Section */}
        <div className="max-w-6xl mx-auto text-center mb-16">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold text-brand-primary mb-6"
            variants={textRevealVariants}
          >
            YKS Koçluğu Nedir?
          </motion.h1>
          <motion.h2
            className="text-lg text-text-secondary leading-relaxed mb-8"
            variants={textRevealVariants}
          >
           Hedeflerinize Ulaşmak İçin Doğru Yol Haritası
          </motion.h2>
          <motion.p 
            className="text-lg text-text-secondary leading-relaxed mb-8"
            variants={textRevealVariants}
          >
Mentörünüz, size en uygun çalışma programını oluşturma görevini üstlenir; sizin tek yapmanız gereken ona güvenmek. Görüşmeler sırasında ihtiyaçlarınız ve çalışma temponuz değerlendirilerek size özel bir çalışma planı oluşturulur. Haftalık olarak detaylandırılmış programlarla hedeflerinize istikrarlı bir şekilde ilerleyebilirsiniz.
          </motion.p>
          <motion.div variants={buttonVariants}>
            <Button 
              variant="default"
              className="bg-brand-primary hover:bg-brand-primary/90 text-white"
              onClick={() => navigate('/coaches')}
            >
              Koçlarımızı İncele
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>

        {/* Key Features Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Birebir Mentörlük */}
              <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5">
                <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-brand-primary mb-2">
                  Birebir Mentörlük
                </h3>
                <p className="text-text-secondary">
                  Derece yapmış mentörünüzle haftalık birebir görüşmeler yaparak hedeflerinize ulaşın.
                </p>
              </div>

              {/* Kişiselleştirilmiş Plan */}
              <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5">
                <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
                  <Calendar className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-brand-primary mb-2">
                  Kişiselleştirilmiş Plan
                </h3>
                <p className="text-text-secondary">
                  İhtiyaçlarınıza özel hazırlanmış günlük ve haftalık çalışma programı ile verimli çalışın.
                </p>
              </div>

              {/* Sürekli İletişim */}
              <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5">
                <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
                  <MessageCircle className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-brand-primary mb-2">
                  Sürekli İletişim
                </h3>
                <p className="text-text-secondary">
                  WhatsApp üzerinden 7/24 soru sorma imkanı ile takıldığınız yerde destek alın.
                </p>
              </div>

              {/* Motivasyon Desteği */}
              <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5">
                <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
                  <Target className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-brand-primary mb-2">
                  Motivasyon Desteği
                </h3>
                <p className="text-text-secondary">
                  Sürekli motivasyon ve hedef odaklı rehberlik ile başarıya odaklanın.
                </p>
              </div>

              {/* Deneyim Aktarımı */}
              <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5">
                <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
                  <GraduationCap className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-brand-primary mb-2">
                  Deneyim Aktarımı
                </h3>
                <p className="text-text-secondary">
                  Bir derece öğrencisinin tecrübelerinden faydalanarak yolunuzu aydınlatın.
                </p>
              </div>

              {/* Deneme Analizleri */}
              <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5">
                <div className="w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center mb-4">
                  <LineChart className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-bold text-brand-primary mb-2">
                  Deneme Analizleri
                </h3>
                <p className="text-text-secondary">
                  Mentörünüzle beraber yapacağınız doğru deneme analizleri ile hatalarınızı saptayın ve eksiklerinize yönelik nokta atışı çalışmayı planlayın.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How We Work Section */}
        <HowWeWork />

        {/* Why YKS Coaching Section */}
        <motion.section 
          variants={itemVariants}
          viewport={{ once: true }}
          className="text-center max-w-4xl mx-auto mb-16 px-4"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-brand-primary">
            Neden YKS Koçluğu Almalısınız?
          </h2>
          <div className="space-y-4">
            <p className="text-lg md:text-xl text-text-secondary">
              YKS hazırlık süreci, doğru rehberlik olmadan oldukça zorlu olabilir. 
              Koçluk hizmeti alarak:
            </p>
            <ul className="space-y-3 text-left max-w-2xl mx-auto">
              {[
                "Zamanınızı daha verimli kullanırsınız",
                "Motivasyonunuzu yüksek tutarsınız",
                "Düzenli çalışma alışkanlığı kazanırsınız",
                "Deneme sınavı analizleriyle eksiklerinizi görürsünüz",
                "Hedeflerinize daha hızlı ulaşırsınız"
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-brand-primary flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.div 
          className="text-center py-16 max-w-4xl mx-auto px-4"
          variants={itemVariants}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-brand-primary">
            Başarıya Giden Yolda İlk Adımı Atın
          </h2>
          <motion.div
            variants={buttonVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              variant="default"
              size="lg"
              className="bg-brand-primary hover:bg-brand-primary/90 text-white"
              onClick={() => navigate('/coaches')}
            >
              Koçlarımızı İncele
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default YksKoclugu;
